<style scoped>
.row-cell {
	cursor: pointer;
	transition: background-color 0.15s ease;
}
.row-cell:hover {
	background-color: rgba(13, 110, 253, 0.05);
}
.cell-value {
	max-width: 320px;
	overflow-wrap: anywhere;
}
</style>

<template>
	<div class="container mt-4">
		<div class="d-flex justify-content-between align-items-center mb-3">
			<h2>
				<RouterLink
					to="/_/review/mapvision"
					class="btn btn-outline-secondary btn-sm me-2"
				>
					<i class="bi bi-arrow-left"></i>
				</RouterLink>
				{{ title }}
			</h2>
		</div>

		<div v-if="data" class="text-muted small mb-3">
			Table <code>{{ data.table }}</code>
			<span v-if="data.description"> — {{ data.description }}</span>
		</div>

		<div v-if="loading" class="text-center py-4">
			<div class="spinner-border text-primary" role="status">
				<span class="visually-hidden">Loading...</span>
			</div>
		</div>

		<div v-else-if="error" class="alert alert-danger">{{ error }}</div>

		<div v-else-if="data && data.rows.length === 0" class="text-muted">
			No rows
		</div>

		<div v-else-if="data" class="card">
			<div class="card-body p-0">
				<div class="table-responsive">
					<table class="table table-hover align-middle mb-0">
						<thead class="table-light">
							<tr>
								<th v-for="column in data.columns" :key="column">
									{{ column }}
								</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="(row, rowIdx) in data.rows"
								:key="String(pks[rowIdx])"
								class="row-cell"
								@click="onRowClick(rowIdx)"
							>
								<td
									v-for="(cell, cellIdx) in row"
									:key="cellIdx"
									class="cell-value"
								>
									<span
										v-if="cell === null || cell === undefined"
										class="text-muted"
										>—</span
									>
									<code v-else-if="typeof cell === 'object'">{{
										JSON.stringify(cell)
									}}</code>
									<span v-else>{{ cell }}</span>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>

			<div
				class="card-footer d-flex justify-content-between align-items-center"
			>
				<button
					class="btn btn-outline-secondary btn-sm"
					:disabled="offset <= 0"
					@click="goPrev"
				>
					<i class="bi bi-chevron-left"></i> Prev
				</button>
				<span class="text-muted small">
					{{ offset + 1 }} – {{ offset + data.rows.length }} of {{ data.total }}
				</span>
				<button
					class="btn btn-outline-secondary btn-sm"
					:disabled="offset + data.rows.length >= data.total"
					@click="goNext"
				>
					Next <i class="bi bi-chevron-right"></i>
				</button>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { apiClient } from "@/client";
import type {
	MapvisionReviewListRow,
	MapvisionReviewType,
	MapReviewQueryBody,
	MapReviewQueryEnvelope,
} from "@/type/api";

const route = useRoute();
const router = useRouter();

const loading = ref(false);
const error = ref("");
/** The page in hand, as `QUERY /api/mapvision/type/{type}` answers it. */
const page = ref<MapReviewQueryEnvelope<MapvisionReviewListRow> | null>(null);
/** The type's catalog entry: its label, table and description. A QUERY page
 * carries rows, not the type's own description of itself, so the catalog read the
 * index already uses is where the page's heading comes from — unchanged until
 * Phase 5, and fetched once per session because it is a function of the server's
 * allowlist. */
const catalog = ref<MapvisionReviewType | null>(null);
const limit = ref(50);
const offset = ref(0);

/** What the browser renders: the type's own title, table and description from
 * the catalog, and the page's columns, rows and count. `columns` and `rows` come
 * from the page (a review type's shape is data, shipped with its rows), the
 * catalog fills the heading, and `total` is the envelope's count of the
 * collection — the read states no narrowing, so the filtered total is the table's
 * size. */
interface RowsView {
	table: string;
	label: string;
	description: string;
	total: number;
	columns: string[];
	rows: unknown[][];
}

const typeName = computed(() => route.params.type as string);

/** The type's columns, in the table's own order, read from the first row's
 * object: a review type's shape is data (the resource discovers it from the
 * table, as the legacy browser did), so the header row is the row's own keys, in
 * the order the row marshals them. An empty page draws no table, so it needs
 * none. */
const columns = computed<string[]>(() =>
	page.value?.items.length ? Object.keys(page.value.items[0].row) : [],
);

/** The page's rows as the cell arrays the browser renders, in the columns' own
 * order. */
const rows = computed<unknown[][]>(() =>
	(page.value?.items ?? []).map((item) => Object.values(item.row)),
);

/** The browser's view model, assembled from the page and the catalog entry so the
 * table's markup and its paging math are unchanged. */
const data = computed<RowsView | null>(() => {
	if (!page.value) return null;
	return {
		table: catalog.value?.table ?? "",
		label: catalog.value?.label ?? typeName.value,
		description: catalog.value?.description ?? "",
		total: page.value.metadata.filtered_total,
		columns: columns.value,
		rows: rows.value,
	};
});

/** Each row's primary-key value, as the page reports it (`item.pk`). The value
 * rather than an index into the columns: a type with no declared primary key is
 * still keyed by one — the read treats the table's first column as the key so
 * paging stays deterministic — and that key is the value the detail route
 * expects. */
const pks = computed<unknown[]>(() =>
	(page.value?.items ?? []).map((item) => item.pk),
);

const title = computed(() => data.value?.label ?? typeName.value);

/** The catalog answers every type at once; one session's answer is reused. A
 * refusal is not cached, so the next visit retries it. */
let catalogOnce: Promise<MapvisionReviewType[]> | null = null;
function loadCatalog(): Promise<MapvisionReviewType[]> {
	catalogOnce ??= apiClient
		.JSONGet<MapvisionReviewType[]>("/api/mapvision/type")
		.catch((err) => {
			catalogOnce = null;
			throw err;
		});
	return catalogOnce;
}

async function load() {
	loading.value = true;
	error.value = "";
	try {
		const [types, answered] = await Promise.all([
			loadCatalog(),
			apiClient.JSONQuery<MapReviewQueryEnvelope<MapvisionReviewListRow>>(
				`/api/mapvision/type/${encodeURIComponent(typeName.value)}`,
				{
					limit: limit.value,
					offset: offset.value,
				} satisfies MapReviewQueryBody,
			),
		]);
		catalog.value = types.find((t) => t.name === typeName.value) ?? null;
		page.value = answered;
	} catch (e: any) {
		error.value =
			e?.response?.data?.message || e?.message || "Failed to load rows";
		page.value = null;
	} finally {
		loading.value = false;
	}
}

function onRowClick(rowIdx: number) {
	const pk = pks.value[rowIdx];
	if (pk === undefined || pk === null) return;
	router.push(`/_/review/mapvision/type/${typeName.value}/${pk}`);
}

function goPrev() {
	offset.value = Math.max(0, offset.value - limit.value);
}

function goNext() {
	offset.value += limit.value;
}

// Navigating to another type starts over at the first page; when offset was
// already 0 the offset watcher below will not fire, so fetch here.
watch(
	() => route.params.type,
	() => {
		const firstPage = offset.value === 0;
		offset.value = 0;
		if (firstPage) void load();
	},
);

// Refetch when pagination moves.
watch(offset, () => {
	void load();
});

onMounted(() => {
	void load();
});
</script>
